import {
  FollowUpStatus,
  SurveyResultOnPatientFragment,
  ShareStatus,
  TagFragment,
  PatientChecklistEmbeddedFragment,
  Treatment,
  ChecklistStatus,
  UserStatus,
  CompletedStatus,
} from 'types.d';
import { DataFilterDate } from 'modules/dashboard/interfaces';

export type Rule = {
  [key: string]: any | undefined;
  id: string;
  parent: string;
  treatment?: string;
  survey?: string;
  question?: string;
  answer?: string | boolean;
  operator?: string;
  type?: string;
  stage?: string;
  typeFilter?: string;
  status?: FollowUpStatus | string;
  followUpDate?: DataFilterDate | string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  mrn?: string;
  dob?: Date | string;
  email?: string;
  typeFilterPatient?: string;
  operatorPatient?: string;
  assignDate?: DataFilterDate | string;
  assignStatus?: ShareStatus | string;
  stageDate?: DataFilterDate | string;
  dateAnswer?: DataFilterDate | string;
  tag?: TagFragment;
  tagOperator?: string;
  checklist?: string;
  typeCheckList?: string;
  surveyNoti?: string;
  statusSurveyNoti?: string;
  dateRangeSurveyNoti?: DataFilterDate | string;
  followUpType?: string;
  followUpTypeDescp?: string;
  workingStatus?: string;
  patientStatus?: UserStatus;
  navigator?: string;
  navigatorTreatment?: string;
  navigatorSurvey?: string;
  navigatorDate?: DataFilterDate | string;
  moduleId?: string;
  recordDate?: DataFilterDate | string;
};

export type Group = {
  id: string;
  condition: string;
  children?: (Rule | Group)[];
};

export type DataEditSurveyResultInterface = {
  response: SurveyResultOnPatientFragment;
  question: string;
  indexResponse: number;
  version: number;
};

export type Timer = {
  seconds: number;
};

export type TagForImport = { name: string; color: string };

export type TChecklistPatient = Omit<
  PatientChecklistEmbeddedFragment,
  'status'
> & {
  updatedAt: any;
  treatment: Pick<Treatment, 'name' | '__typename'> | null;
  survey: Pick<Treatment, 'name' | '__typename'> | null;
  _id: string;
  status: ChecklistStatus | null;
};

export type ConditionSurvey = {
  survey?: string;
  completedStatus?: string | { [key: string]: string[] };
  workingStatus?: string | { [key: string]: string[] };
};
export type TChecklistPatients = TChecklistPatient[];

/* -------------------------------------------------------------------------
 * IVR / phone survey types.
 *
 * Hand-written because codegen is deferred. Names match what graphql-codegen
 * would emit from the staging schema, so when it is re-run this whole block
 * can be deleted and the imports repointed at `types.d`.
 *
 * Do NOT move these into `src/types.d.tsx` -- codegen.yml sets
 * `overwrite: true` on that file and would silently destroy them.
 * ---------------------------------------------------------------------- */

export enum IvrSurveyPhase {
  Dialing = 'DIALING',
  InCall = 'IN_CALL',
  Completed = 'COMPLETED',
  Partial = 'PARTIAL',
  RetryScheduled = 'RETRY_SCHEDULED',
  Unreachable = 'UNREACHABLE',
  OptedOut = 'OPTED_OUT',
}

export type IvrCallStatusSummary = {
  __typename?: 'IvrCallStatusSummary';
  phase: IvrSurveyPhase;
  description: string;
  surveyResultId: string;
  callSid?: string | null;
  /**
   * Raw Twilio state. Note the generated `IvrCallStatusEnum` is missing
   * RESCHEDULED, which the backend does emit -- do not switch on this
   * exhaustively without a default.
   */
  callStatus?: string | null;
  completedStatus?: CompletedStatus | null;
  answeredCount?: number | null;
  attemptCount?: number | null;
  retriesRemaining?: number | null;
  nextRetryAt?: any | null;
  lastActivityAt?: any | null;
  recordingUrl?: string | null;
  patientOptedOut: boolean;
};

export type IvrCallStatusBySurveyResultQueryVariables = {
  params: { surveyResultId: string };
};

export type IvrCallStatusBySurveyResultQuery = {
  __typename?: 'Query';
  ivrCallStatusBySurveyResult: IvrCallStatusSummary | null;
};

export type IvrCallsForPatientQueryVariables = {
  params: {
    patientId: string;
    limit?: number | null;
    treatmentId?: string | null;
  };
};

export type IvrCallsForPatientQuery = {
  __typename?: 'Query';
  ivrCallsForPatient: IvrCallStatusSummary[];
};

/**
 * `doNotCall` is live on the Patient type but absent from the generated
 * fragment, since the fragment document is only being extended now.
 */
export type OwnerWithOptOut = {
  doNotCall?: boolean | null;
  doNotCallAt?: any | null;
  doNotCallReason?: string | null;
};

/** Phases still expected to change, so the UI should keep polling. */
export const IVR_LIVE_PHASES: IvrSurveyPhase[] = [
  IvrSurveyPhase.Dialing,
  IvrSurveyPhase.InCall,
];
